import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Shield, CheckCircle, MapPin, MessageSquare, Camera,
  Stethoscope, FileText, Clock, CreditCard, ChevronRight,
  UserCheck, Mail, ExternalLink, Menu, X,
  ChevronDown, HelpCircle, Monitor, ArrowRight, Smartphone, Star
} from 'lucide-react';

const LOGO_URL = '/logo-clicdoc.webp';
const TERMS_URL = 'https://customer-assets.emergentagent.com/job_e6dd68a9-7d3a-4e64-a527-bd284f14f76e/artifacts/fdyci7he_ClicDoc_Termos_de_Uso.docx.pdf';

const steps = [
  { num: '1', title: 'Crie sua Conta', desc: 'Rápido e seguro, apenas dados básicos', icon: UserCheck },
  { num: '2', title: 'Descreva o Motivo', desc: 'Conte ao médico o que está sentindo', icon: MessageSquare },
  { num: '3', title: 'Pagamento Fácil', desc: 'R$ 59 no cartão ou PIX', icon: CreditCard },
  { num: '4', title: 'Inicie o Chat', desc: 'Atendimento imediato com médico clínico geral', icon: Monitor },
];

const advantages = [
  { title: 'Sem Baixar Aplicativo', desc: 'Acesse direto pelo navegador do celular ou computador. Sem instalar nada, sem ocupar espaço no aparelho. É só abrir e consultar.', badge: 'Diferencial', icon: Smartphone },
  { title: 'Preço Acessível', desc: 'Apenas R$ 59 por consulta, sem mensalidades ou taxas escondidas', badge: 'Melhor custo-benefício', icon: CreditCard },
  { title: 'Sem Filas', desc: 'Atendimento direto pelo celular, quando e onde você precisar', icon: Clock },
  { title: 'Receitas e Atestados', desc: 'Documentos com validade em todo o território nacional', icon: FileText },
  { title: 'Médicos Verificados', desc: 'Profissionais com CRM ativo e verificado', icon: UserCheck },
  { title: 'Atendimento por Chat', desc: 'Ideal para infecções comuns, renovação de receitas, atestados e orientações. O médico redireciona para atendimento presencial se necessário.', icon: MessageSquare },
  { title: 'Envie Fotos', desc: 'Compartilhe imagens e documentos durante a consulta', icon: Camera },
  { title: 'Todo o Brasil', desc: 'Disponível em qualquer lugar do território nacional', icon: MapPin },
];

const pricingFeatures = [
  'Consulta totalmente por chat',
  'Receita digital válida em todo Brasil',
  'Atestado médico (se necessário)',
  'Envio de fotos e documentos',
  'Pagamento seguro via cartão',
];

const faqItems = [
  { q: 'Quanto custa uma consulta no ClicDoc?', a: 'A consulta custa apenas R$ 59,00, valor único. Pagamento por cartão de crédito ou PIX.' },
  { q: 'A receita digital é válida em farmácias?', a: 'Sim. As receitas emitidas pelo ClicDoc possuem validade legal em todo o território nacional, conforme regulamentação do CFM.' },
  { q: 'E se nenhum médico atender? Tenho reembolso?', a: 'Sim. Se a consulta não ocorrer, você recebe reembolso integral. O valor é devolvido automaticamente ao método de pagamento original.' },
  { q: 'A consulta por chat é adequada ao meu problema?', a: 'O chat funciona muito bem para infecções comuns, renovação de receitas, atestados, orientações sobre medicamentos e quadros que não exigem exame físico. Para emergências, dores intensas no peito ou situações que precisam de exame presencial, procure um pronto-socorro — o próprio médico do ClicDoc irá orientá-lo se for o caso.' },
  { q: 'Preciso agendar a consulta?', a: 'Não. O atendimento é por demanda. Basta acessar o site, solicitar e um médico disponível iniciará o chat em poucos minutos. Fora do horário de atendimento (6h às 19h, horário de Brasília), sua solicitação ficará na fila e será atendida assim que um médico estiver disponível.' },
  { q: 'O ClicDoc emite atestado médico?', a: 'Sim, quando clinicamente necessário, o médico pode emitir atestado médico digital com validade em todo o Brasil.' },
  { q: 'Quais especialidades estão disponíveis?', a: 'Atualmente oferecemos consultas com médicos clínicos gerais, que podem orientar sobre diversas condições de saúde.' },
  { q: 'Preciso baixar algum aplicativo?', a: 'Não! O ClicDoc funciona 100% pelo navegador do celular ou computador. Não precisa instalar nada e não ocupa espaço no seu aparelho.' },
  { q: 'O ClicDoc funciona em todo o Brasil?', a: 'Sim. Por ser 100% online, você pode consultar de qualquer lugar do Brasil com acesso à internet.' },
  { q: 'Meus dados estão seguros?', a: 'Sim. Todos os dados são protegidos com criptografia e seguimos a LGPD. O sigilo médico é garantido.' },
  { q: 'O ClicDoc atende menores de idade?', a: 'Não. O ClicDoc atende exclusivamente pacientes adultos, com 18 anos ou mais. Menores de idade devem buscar atendimento presencial acompanhados de um responsável.' },
];

function PhoneMockup() {
  return (
    <div className="relative mx-auto" style={{ width: 280 }}>
      <div className="relative bg-white rounded-[2.5rem] border-[6px] border-gray-900 overflow-hidden shadow-2xl" style={{ height: 560 }}>
        <div className="bg-gray-900 text-white text-[10px] flex items-center justify-between px-6 py-1.5">
          <span>9:41</span>
          <div className="absolute left-1/2 -translate-x-1/2 w-24 h-5 bg-gray-900 rounded-b-xl" />
          <div className="flex gap-1 items-center">
            <div className="w-3 h-2 border border-white rounded-sm"><div className="w-2 h-1.5 bg-green-400 rounded-sm m-px" /></div>
          </div>
        </div>
        {/* Realistic chat header */}
        <div className="bg-[#2563eb] text-white px-4 py-2.5 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <Stethoscope className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-xs font-semibold">Consulta em andamento</p>
            <p className="text-[10px] text-blue-200">Médico conectado</p>
          </div>
        </div>
        {/* Realistic chat messages */}
        <div className="flex flex-col gap-2 px-3 py-3 text-xs overflow-hidden" style={{ height: 340 }}>
          <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-3 py-2 max-w-[82%] text-gray-700">
            Olá! Vi que você está com dor de garganta há 2 dias. Tem febre?
          </div>
          <div className="bg-[#2563eb] text-white rounded-2xl rounded-tr-sm px-3 py-2 max-w-[78%] ml-auto">
            Sim, 38.2° ontem à noite.
          </div>
          <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-3 py-2 max-w-[82%] text-gray-700">
            Certo. Vou prescrever o tratamento. Já envio a receita digital aqui no chat.
          </div>
          {/* Prescription card */}
          <div className="bg-green-50 border border-green-200 rounded-xl px-3 py-2.5 max-w-[85%]">
            <div className="flex items-center gap-1.5 mb-1.5">
              <FileText className="w-3.5 h-3.5 text-green-600" />
              <span className="text-[10px] font-semibold text-green-800">Receita Digital</span>
            </div>
            <p className="text-[10px] text-green-700">Amoxicilina 500mg — 1cp 8/8h por 7 dias</p>
            <div className="flex items-center gap-1 mt-1.5">
              <Shield className="w-2.5 h-2.5 text-green-500" />
              <span className="text-[9px] text-green-600">Assinatura digital ICP-Brasil</span>
            </div>
          </div>
          <div className="bg-[#2563eb] text-white rounded-2xl rounded-tr-sm px-3 py-2 max-w-[78%] ml-auto">
            Recebi, obrigado doutor!
          </div>
        </div>
        {/* Chat input */}
        <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-3 py-2.5 flex items-center gap-2">
          <div className="flex-1 bg-gray-100 rounded-full px-3 py-2 text-[10px] text-gray-400">Digite sua mensagem...</div>
          <div className="w-7 h-7 bg-[#2563eb] rounded-full flex items-center justify-center">
            <ArrowRight className="w-3.5 h-3.5 text-white" />
          </div>
        </div>
      </div>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
        data-testid={`faq-item-${q.substring(0, 20).replace(/\s/g, '-')}`}
      >
        <span className="font-medium text-gray-800 text-sm pr-4">{q}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm text-gray-500 leading-relaxed">{a}</div>
      )}
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [showFloatingCta, setShowFloatingCta] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowFloatingCta(window.scrollY > 600);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load Reclame Aqui verified seal (lazy — only when footer is visible)
  const raRef = useRef(null);
  useEffect(() => {
    const el = raRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        observer.disconnect();
        const id = 'ra-embed-verified-seal';
        if (!document.getElementById(id)) {
          const s = document.createElement('script');
          s.id = id;
          s.src = 'https://s3.amazonaws.com/raichu-beta/ra-verified/bundle.js';
          s.setAttribute('data-id', 'RGxuS0I3UF8tblpKWTdNaTpzZWJlbi1lLW9saXZlc2tpLXVyb2xvZ2lhLWUtZ2luZWNvbG9naWEtc29jaWVkYWRlLXNpbXBsZXM=');
          s.setAttribute('data-target', 'ra-verified-seal');
          s.setAttribute('data-model', 'horizontal_1');
          document.body.appendChild(s);
        }
      }
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // TikTok Pixel: ViewContent on landing page
  useEffect(() => {
    if (window.ttq) {
      window.ttq.track('ViewContent', {
        contents: [{ content_id: 'consulta_medica', content_type: 'product', content_name: 'Consulta Médica Online ClicDoc' }],
        value: 59, currency: 'BRL'
      });
    }
  }, []);

  return (
    <main className="min-h-screen bg-white" style={{ fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <Helmet>
        <title>Consulte um Médico Online Agora — R$ 59 | ClicDoc</title>
        <meta name="description" content="Médico disponível agora. Consulta online por chat, receita e atestado digital em minutos. R$ 59, sem fila, sem app. Reembolso se não for atendido." />
        <link rel="canonical" href="https://clicdoc.com.br/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://clicdoc.com.br/" />
        <meta property="og:title" content="Consulte um Médico Online Agora — R$ 59 | ClicDoc" />
        <meta property="og:description" content="Médico disponível agora. Consulta online por chat, receita e atestado digital em minutos. R$ 59, sem fila, sem app." />
        <meta property="og:image" content="https://clicdoc.com.br/logo-clicdoc.webp" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="ClicDoc" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Consulte um Médico Online Agora — R$ 59 | ClicDoc" />
        <meta name="twitter:description" content="Médico disponível agora. Consulta online por chat, receita e atestado digital em minutos." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "ClicDoc",
          "url": "https://clicdoc.com.br",
          "logo": "https://clicdoc.com.br/logo-clicdoc.webp",
          "description": "Plataforma brasileira de telemedicina. Consulta médica online por chat com receita e atestado digital.",
          "foundingDate": "2024",
          "founder": { "@type": "Person", "name": "Dr. Rodrigo Seben" },
          "contactPoint": { "@type": "ContactPoint", "email": "contato@clicdoc.com.br", "contactType": "customer service" },
          "sameAs": [
            "https://www.linkedin.com/in/rodrigoseben",
            "https://wa.me/555133765090",
            "https://www.reclameaqui.com.br/empresa/seben-e-oliveski-urologia-e-ginecologia-sociedade-simples/"
          ]
        })}</script>
      </Helmet>
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100" role="navigation" aria-label="Navegação principal" data-testid="landing-navbar">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/"><img src={LOGO_URL} alt="ClicDoc — Consulta Médica Online" className="h-16" width="156" height="64" /></a>
          <div className="hidden md:flex items-center gap-8">
            <a href="#como-funciona" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Como Funciona</a>
            <a href="#vantagens" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Vantagens</a>
            <a href="#preco" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Preço</a>
            <a href="#faq" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">FAQ</a>
            <Link to="/blog" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Blog</Link>
            <button onClick={() => navigate('/medico')} className="text-sm text-gray-500 hover:text-gray-700 transition-colors">Área do Médico</button>
            <button onClick={() => navigate('/login')}
              className="px-6 py-2.5 bg-[#2563eb] text-white rounded-full text-sm font-semibold hover:bg-[#1d4ed8] transition-colors shadow-md"
              data-testid="nav-consultar-btn">
              Consultar Agora - R$ 59
            </button>
          </div>
          <button className="md:hidden p-2" onClick={() => setMobileMenu(!mobileMenu)}>
            {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenu && (
          <div className="md:hidden bg-white border-t px-4 py-4 space-y-3">
            <a href="#como-funciona" onClick={() => setMobileMenu(false)} className="block text-sm text-gray-600">Como Funciona</a>
            <a href="#vantagens" onClick={() => setMobileMenu(false)} className="block text-sm text-gray-600">Vantagens</a>
            <a href="#preco" onClick={() => setMobileMenu(false)} className="block text-sm text-gray-600">Preço</a>
            <a href="#faq" onClick={() => setMobileMenu(false)} className="block text-sm text-gray-600">FAQ</a>
            <Link to="/blog" onClick={() => setMobileMenu(false)} className="block text-sm text-gray-600">Blog</Link>
            <button onClick={() => navigate('/medico')} className="block text-sm text-gray-500">Área do Médico</button>
            <button onClick={() => { navigate('/login'); setMobileMenu(false); }} className="block text-center w-full px-5 py-2.5 bg-[#2563eb] text-white rounded-full text-sm font-semibold">Consultar Agora</button>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-gray-50 to-white" data-testid="landing-hero" aria-label="Consulta médica online">
        <div className="max-w-6xl mx-auto px-4 flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          <div className="flex-1 max-w-xl">
            {(() => {
              const nowBRT = new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo', hour: 'numeric', hour12: false });
              const hour = parseInt(nowBRT, 10);
              const isOpen = hour >= 6 && hour < 19;
              return isOpen ? (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full mb-6">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-sm font-medium text-green-700">Médicos disponíveis agora</span>
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-full mb-6">
                  <Clock className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">Atendimento diário das 6h às 19h</span>
                </div>
              );
            })()}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-4">
              Consulte um médico online agora por <span className="text-[#16a34a] whitespace-nowrap">R$ 59</span>
            </h1>
            {/* Rating badge — D3.1, D3.2 */}
            <div className="flex items-center gap-2 mb-6">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(s => <Star key={s} className={`w-4 h-4 ${s <= 4 ? 'text-yellow-400 fill-yellow-400' : 'text-yellow-400 fill-yellow-400 opacity-80'}`} />)}
              </div>
              <span className="text-sm font-semibold text-gray-700">4.8</span>
              <span className="text-sm text-gray-400">· mais de 500 consultas realizadas</span>
            </div>
            <ul className="grid grid-cols-2 gap-3 mb-4">
              <li className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-xl px-4 py-3 text-sm font-medium text-gray-700">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /> Direto pelo navegador
              </li>
              <li className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-xl px-4 py-3 text-sm font-medium text-gray-700">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /> Sem instalar aplicativo
              </li>
              <li className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-xl px-4 py-3 text-sm font-medium text-gray-700">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /> Chat em tempo real
              </li>
              <li className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-xl px-4 py-3 text-sm font-medium text-gray-700">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /> Receita digital válida
              </li>
              <li className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-xl px-4 py-3 text-sm font-medium text-gray-700">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /> Consulta com atestado
              </li>
              <li className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-xl px-4 py-3 text-sm font-medium text-gray-700">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /> Médicos com CRM
              </li>
              <li className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-xl px-4 py-3 text-sm font-medium text-gray-700">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /> Cartão ou PIX
              </li>
            </ul>
            <div className="flex items-center gap-2 mb-8 text-sm text-gray-500" data-testid="landing-hours">
              <Clock className="w-4 h-4 text-blue-500" />
              <span>Atendimento todos os dias, das <strong className="text-gray-700">6h às 19h</strong> (horário de Brasília)</span>
            </div>
            <button onClick={() => navigate('/login')}
              className="px-8 py-4 bg-[#16a34a] text-white rounded-full text-base font-bold hover:bg-[#15803d] transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
              data-testid="hero-consultar-btn">
              Consultar Agora - R$ 59 <ArrowRight className="w-5 h-5" />
            </button>
            <a href="#como-funciona" className="inline-flex items-center gap-1.5 mt-3 text-sm text-[#2563eb] hover:text-[#1d4ed8] font-medium transition-colors" data-testid="hero-secondary-cta">
              Veja como funciona <ChevronDown className="w-4 h-4" />
            </a>
            <p className="text-xs text-gray-400 mt-2">Pagamento seguro por cartão ou PIX. Atendimento em minutos.</p>
          </div>
          <div className="flex-shrink-0">
            <PhoneMockup />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="como-funciona" className="py-20 bg-white" data-testid="landing-how-it-works" aria-label="Como funciona">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Como funciona o ClicDoc?</h2>
            <p className="text-base text-gray-500 max-w-2xl mx-auto">O processo para consultar um médico online pelo ClicDoc é simples e leva poucos minutos. Você cria sua conta, descreve o motivo da consulta, realiza o pagamento de R$ 59 por cartão ou PIX, e inicia o atendimento por chat com um clínico geral disponível.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s) => (
              <div key={s.num} className="relative p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all group">
                <div className="w-10 h-10 bg-[#2563eb] text-white rounded-xl flex items-center justify-center font-bold text-lg mb-4">{s.num}</div>
                <h3 className="font-semibold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section id="vantagens" className="py-20 bg-gray-50" data-testid="landing-advantages" aria-label="Vantagens">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Por que escolher o ClicDoc?</h2>
            <p className="text-base text-gray-500 max-w-2xl mx-auto">O ClicDoc se diferencia por oferecer atendimento médico de qualidade a um preço acessível, sem burocracia. Funciona direto pelo navegador, sem instalar aplicativo, com médicos verificados pelo CRM e documentos digitais válidos em todo o território nacional.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {advantages.map((a) => {
              const Icon = a.icon;
              return (
                <div key={a.title} className="p-6 bg-white rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all">
                  <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#2563eb]" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{a.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{a.desc}</p>
                  {a.badge && <span className="inline-block mt-3 text-xs font-medium text-[#2563eb] bg-blue-50 px-2.5 py-1 rounded-full">{a.badge}</span>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="preco" className="py-20 bg-white" data-testid="landing-pricing" aria-label="Preço">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Sem mensalidades. Pague apenas quando usar.</h2>
            <p className="text-base text-gray-500 max-w-2xl mx-auto">O ClicDoc não cobra mensalidade, taxa de adesão nem valores adicionais. Cada consulta custa R$ 59, valor único que inclui o atendimento completo por chat, receita digital e atestado médico quando necessário. O pagamento pode ser feito por cartão de crédito ou à vista por PIX.</p>
          </div>
          <div className="max-w-md mx-auto">
            <div className="relative bg-white rounded-3xl border-2 border-[#2563eb] p-8 shadow-lg">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#2563eb] text-white text-xs font-bold px-4 py-1 rounded-full">PREÇO ÚNICO</div>
              <p className="text-sm text-gray-500 mb-4 text-center">Consulta completa por apenas</p>
              <div className="flex items-baseline justify-center gap-1 mb-1">
                <span className="text-lg text-gray-500">R$</span>
                <span className="text-6xl font-extrabold text-gray-900">59</span>
                <span className="text-lg text-gray-500">,00</span>
              </div>
              <p className="text-sm text-gray-400 text-center mb-8">Pagamento único por cartão ou PIX</p>
              <ul className="space-y-3 mb-8">
                {pricingFeatures.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate('/login')}
                className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#16a34a] text-white rounded-xl font-semibold hover:bg-[#15803d] transition-colors">
                Consultar Agora <ChevronRight className="w-4 h-4" />
              </button>
              <p className="text-xs text-center text-green-700 bg-green-50 rounded-lg py-2 px-3 mt-3">
                Reembolso integral se a consulta não ocorrer
              </p>
              <div className="flex items-center justify-center gap-6 mt-3">
                <span className="flex items-center gap-1.5 text-xs text-gray-400"><Shield className="w-3 h-3" /> Pagamento seguro</span>
                <span className="flex items-center gap-1.5 text-xs text-gray-400"><Clock className="w-3 h-3" /> Atendimento rápido</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Depoimentos */}
      <section className="py-20 bg-white" data-testid="landing-testimonials" aria-label="Depoimentos">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-50 border border-yellow-200 rounded-full mb-4">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span className="text-sm font-medium text-yellow-700">Avaliações de pacientes</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">O que dizem nossos pacientes</h2>
            <p className="text-base text-gray-500">Mais de 500 consultas realizadas com satisfação</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: 'Camila R.', city: 'São Paulo, SP', context: 'professora', text: 'Estava com infecção urinária e precisava de receita urgente. Em menos de 15 minutos já tinha a receita digital no celular. Fui direto à farmácia. Incrível!', stars: 5 },
              { name: 'Marcos A.', city: 'Belo Horizonte, MG', context: 'pai de família', text: 'Usei num domingo à noite quando meu filho teve febre. O médico foi super atencioso, pediu todos os detalhes e passou a medicação certinha. Recomendo demais.', stars: 5 },
              { name: 'Juliana S.', city: 'Curitiba, PR', context: 'analista de TI', text: 'Precisava de atestado para o trabalho e não queria esperar horas num pronto-socorro. Consulta rápida, médico com CRM verificado. O atestado digital foi aceito sem problemas.', stars: 5 },
              { name: 'Roberto L.', city: 'Porto Alegre, RS', context: 'aposentado', text: 'Achei o preço muito justo. R$ 59 por uma consulta completa com receita digital. Na farmácia popular ainda economizei no remédio. Melhor custo-benefício que já encontrei.', stars: 5 },
              { name: 'Fernanda M.', city: 'Recife, PE', context: 'mora no interior', text: 'Moro no interior e o posto de saúde mais perto fica a 40km. O ClicDoc resolveu meu problema em casa mesmo, pelo celular. Atendimento humanizado e eficiente.', stars: 5 },
              { name: 'Lucas T.', city: 'Florianópolis, SC', context: 'motorista de app', text: 'Sou motorista de app e não posso perder tempo em filas. Consultei entre uma corrida e outra, pelo chat mesmo. O médico entendeu minha rotina e foi objetivo. Nota 10!', stars: 4 },
            ].map((t, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-shadow" data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className={`w-4 h-4 ${s < t.stars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} />
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{t.name}</p>
                    <p className="text-xs text-gray-400">{t.context} · {t.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Responsible Doctor — D3.3 */}
      <section id="sobre" className="py-16 bg-white" aria-label="Médico Responsável" data-testid="doctor-section">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center gap-8 bg-gray-50 rounded-2xl p-8 border border-gray-100">
            <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Stethoscope className="w-10 h-10 text-[#2563eb]" />
              {/* TODO: Replace with real photo of Dr. Rodrigo Seben */}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Dr. Rodrigo Seben</h3>
              <p className="text-sm text-[#2563eb] font-medium mb-3">Médico Responsável · CRM/RS 30092</p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Criei o ClicDoc porque vi como é difícil conseguir uma consulta rápida no Brasil. Muita gente perde horas em filas ou dias esperando agendamento — às vezes só para renovar uma receita. O ClicDoc existe para resolver isso: um médico de verdade, com CRM ativo, atendendo por chat em minutos. Sem burocracia, sem app para instalar, a um preço justo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 bg-gray-50" data-testid="landing-faq" aria-label="Perguntas frequentes">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full mb-4">
              <HelpCircle className="w-4 h-4 text-[#2563eb]" />
              <span className="text-sm font-medium text-[#2563eb]">Dúvidas frequentes</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Perguntas Frequentes</h2>
            <p className="text-base text-gray-500">Tudo que você precisa saber antes de consultar</p>
          </div>
          <div className="space-y-3">
            {faqItems.map((item) => (
              <FaqItem key={item.q} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0f1e3d] to-[#1e40af] text-white" data-testid="landing-cta">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Não espere mais. Consulte agora.</h2>
          <p className="text-base text-blue-200 mb-10">
            Médico disponível neste momento. Consulta por chat, receita e atestado em minutos. Tudo pelo celular, sem baixar nada.
          </p>
          <button onClick={() => navigate('/login')}
            className="inline-flex items-center gap-3 px-10 py-5 bg-green-500 hover:bg-green-600 text-white rounded-full font-bold text-lg transition-colors shadow-lg hover:shadow-xl"
            data-testid="cta-consultar-online">
            Consultar Agora - R$ 59
          </button>
          <p className="text-sm text-blue-300 mt-6">Pagamento seguro. Atendimento imediato com clínico geral.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row items-start justify-between gap-8 mb-10">
            <div>
              <img src={LOGO_URL} alt="ClicDoc" className="h-16 mb-3" width="156" height="64" loading="lazy" />
              <p className="text-sm text-gray-400 max-w-xs">Consultas médicas online acessíveis para todos os brasileiros.</p>
            </div>
            <div className="flex flex-wrap gap-6 items-center">
              <a href="mailto:contato@clicdoc.com.br" className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
                <Mail className="w-4 h-4" /> contato@clicdoc.com.br
              </a>
              <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
                <ExternalLink className="w-4 h-4" /> Termos de Uso e Privacidade
              </a>
            </div>
          </div>
          {/* SEO cross-links */}
          <div className="flex flex-wrap gap-3 mb-8">
            <Link to="/consulta-medica-online" className="text-xs text-gray-400 hover:text-blue-600 transition-colors">Consulta Médica Online</Link>
            <Link to="/receita-digital" className="text-xs text-gray-400 hover:text-blue-600 transition-colors">Receita Digital</Link>
            <Link to="/atestado-medico-online" className="text-xs text-gray-400 hover:text-blue-600 transition-colors">Atestado Médico Online</Link>
            <Link to="/medico-online-agora" className="text-xs text-gray-400 hover:text-blue-600 transition-colors">Médico Online Agora</Link>
            <Link to="/blog" className="text-xs text-gray-400 hover:text-blue-600 transition-colors">Blog de Saúde</Link>
            <Link to="/autor/dr-rodrigo-seben" className="text-xs text-gray-400 hover:text-blue-600 transition-colors">Nosso Médico</Link>
          </div>
          <div className="border-t border-gray-100 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-gray-400">2026 ClicDoc. Todos os direitos reservados.</p>
            <div className="flex items-center gap-4">
              <a href="https://www.reclameaqui.com.br/rav/t5CD" target="_blank" rel="noopener noreferrer"
                className="flex items-center" data-testid="reclameaqui-seal">
                <div id="ra-verified-seal" ref={raRef}></div>
              </a>
              <a href="https://safeweb.norton.com/report/show?url=clicdoc.com.br" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors" data-testid="norton-seal">
                <Shield className="w-3.5 h-3.5" />
                <span>Norton Safe Web</span>
              </a>
            </div>
          </div>
          <p className="text-center text-[11px] text-gray-300 mt-6 max-w-2xl mx-auto leading-relaxed">
            O ClicDoc oferece consultas médicas online com clínicos gerais. Nosso serviço não substitui atendimento de emergência. Em caso de urgência, procure o pronto-socorro mais próximo.
          </p>
          {/* Social links */}
          <div className="flex items-center justify-center gap-4 mt-5">
            <a href="https://www.instagram.com/_clicdoc_" target="_blank" rel="noopener noreferrer" aria-label="Instagram ClicDoc" className="text-gray-400 hover:text-pink-500 transition-colors" data-testid="social-instagram">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            </a>
            <a href="https://www.facebook.com/profile.php?id=61595771975405" target="_blank" rel="noopener noreferrer" aria-label="Facebook ClicDoc" className="text-gray-400 hover:text-blue-600 transition-colors" data-testid="social-facebook">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a href="https://www.tiktok.com/@clicdoc" target="_blank" rel="noopener noreferrer" aria-label="TikTok ClicDoc" className="text-gray-400 hover:text-gray-100 transition-colors" data-testid="social-tiktok">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.46V13.2a8.16 8.16 0 005.58 2.17v-3.45a4.85 4.85 0 01-2.98-1.04V6.69h2.98z"/></svg>
            </a>
          </div>
          <p className="text-center text-sm text-gray-400 mt-5">Desde 2025, consultas médicas acessíveis para todos os brasileiros.</p>
          <p className="text-center text-[10px] text-gray-300 mt-3"><a href="#sobre" className="hover:text-gray-500 transition-colors">Responsável Técnico: Dr. Rodrigo Seben — CRM/RS 30092</a></p>
          <p className="text-center text-[10px] text-gray-300 mt-1">CNPJ 15.472.291/0001-98</p>
        </div>
      </footer>

      {/* WhatsApp floating button */}
      <a
        href="https://wa.me/555133765090"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-36 md:bottom-6 right-4 z-[60] w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:bg-[#1ebe5a] hover:scale-110 transition-all animate-bounce-slow"
        aria-label="Contato via WhatsApp"
        data-testid="whatsapp-float-btn"
      >
        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {/* Floating CTA - mobile */}
      {showFloatingCta && (
        <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white/95 backdrop-blur border-t border-gray-200 px-4 py-3 safe-area-bottom" data-testid="floating-cta">
          <button onClick={() => navigate('/login')}
            className="flex items-center justify-center gap-2 w-full py-3 bg-[#16a34a] text-white rounded-xl font-semibold text-sm hover:bg-[#15803d] transition-colors shadow-lg">
            Consultar Agora - R$ 59
          </button>
        </div>
      )}
    </main>
  );
}
